import React, { useState, useCallback } from "react";
import {
  WeightManagementPage,
  AdminWeightManagementTable,
  type WeightManagementRecord,
} from "@racoon/ui";
import { useWeightManagementAdminData } from "../model/use-weight-management-admin-data";
import { useRouteJobsModal } from "~/pages/dashboard/routes/hooks/useRouteJobsModal";
import { useLazyGetRouteQuery } from "~/shared/api/routes/route.grpc";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { useSearchParams } from "react-router-dom";
import { useLang } from "@racoon/i18n";
import { ScaleRecordModal } from "../../dashboard/routes/components/ScaleRecordModal";
import { useUserCard } from "~/pages/dashboard/users/ui/use-user-card";
import { useShowOnDashboard } from "~/shared/hooks/useShowOnDashboard";

export const WeightManagementAdminPage: React.FC = () => {
  const [fetchRoute] = useLazyGetRouteQuery();
  const { activeCompany } = useAuthSession();
  const { openRouteJobs } = useRouteJobsModal();

  const [selectedRecordForPhotos, setSelectedRecordForPhotos] =
    useState<WeightManagementRecord | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const highlightRecordId = searchParams.get("highlightRecord");
  const { lang } = useLang() || { lang: "ru" };

  const locale = String(lang).includes("en") ? "en-GB" : "ru-RU";

  const formatTimeRoute = useCallback(
    (dateStr?: string | Date) => {
      if (!dateStr) return "--:--";
      return new Date(dateStr).toLocaleTimeString(locale, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    },
    [locale],
  );

  const formatDate = useCallback(
    (dateStr?: string | Date) => {
      if (!dateStr) return "--.--.--";
      const formattedDate = new Date(dateStr).toLocaleDateString(locale, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
      return formattedDate.replace(" г.", "");
    },
    [locale],
  );

  const handleRouteClick = (route: any) => {
    if (route?.id)
      fetchRoute({ id: BigInt(route.id), ownerOrganizationId: BigInt(activeCompany?.id ?? 0) })
        .unwrap()
        .then((r) => {
          if (r) openRouteJobs(r);
        })
        .catch((error) => console.error("Failed to fetch route:", error));
  };

  const handleClearHighlight = () => {
    if (highlightRecordId) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("highlightRecord");
      setSearchParams(newParams, { replace: true });
    }
  };
  const handlePhotosClick = (record: WeightManagementRecord) => {
    setSelectedRecordForPhotos(record);
  };

  // The registry's table lives in @racoon/ui, so the cards it can open are
  // handed to it the same way the route link already is.
  const { openUser, userCard } = useUserCard();
  const { showTruck } = useShowOnDashboard();

  return (
    <>
      <WeightManagementPage
        useData={useWeightManagementAdminData}
        hideStates={true}
        filterBlockDirection="row"
        renderTable={(props: any) => (
          <AdminWeightManagementTable
            {...props}
            highlightRecordId={highlightRecordId}
            onRowClick={handleClearHighlight}
            onRouteClick={handleRouteClick}
            onPhotosClick={handlePhotosClick}
            onPlateClick={showTruck}
            onDriverClick={openUser}
          />
        )}
      />

      {selectedRecordForPhotos && (
        <ScaleRecordModal
          open={!!selectedRecordForPhotos}
          onOpenChange={(isOpen) => {
            if (!isOpen) setSelectedRecordForPhotos(null);
          }}
          scaleRecord={selectedRecordForPhotos}
          formatTimeRoute={formatTimeRoute}
          formatDate={formatDate}
        />
      )}
      {userCard}
    </>
  );
};
